import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowRight, Sparkles, Truck, ShieldCheck, ClipboardList, Layers, LogIn } from 'lucide-react';
import { IDS } from '@/constants/testIds';
import api from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';

function categoryTile(palette = 0) {
  const palettes = [
    ['#FBF0F6', '#F2A6CE', '#C2478A'],
    ['#F5E9EE', '#E8B5CE', '#9E2E6E'],
    ['#F9F5F2', '#F0D6DE', '#C2478A'],
    ['#EFE9EE', '#DDBFD1', '#7A2F58'],
  ];
  const [bg, mid, accent] = palettes[palette % palettes.length];
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 800'>
  <defs>
    <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
      <stop offset='0' stop-color='${bg}'/>
      <stop offset='0.55' stop-color='${mid}'/>
      <stop offset='1' stop-color='${accent}'/>
    </linearGradient>
  </defs>
  <rect width='600' height='800' fill='url(#g)'/>
  <g opacity='0.12' fill='#0A0A0A'>
    <circle cx='300' cy='420' r='240'/>
  </g>
  <g font-family='Georgia, serif' text-anchor='middle' fill='#0A0A0A' opacity='0.32'>
    <text x='300' y='360' font-size='120' font-style='italic' letter-spacing='-3'>IJE</text>
    <text x='300' y='420' font-size='38' letter-spacing='14'>HAUS</text>
  </g>
</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

const CATEGORIES = [
  { name: 'Dildos', image: categoryTile(0) },
  { name: 'Butt Plugs', image: categoryTile(1) },
  { name: 'Male Enhancement', image: categoryTile(2) },
  { name: 'Lubricants', image: categoryTile(3) },
];

export default function Home() {
  const { user } = useAuth();
  // Pull live brand + category count from the catalog so the homepage stays
  // truthful as the assortment grows beyond any single supplier.
  const [meta, setMeta] = useState({ brands: [], categories: [], total: 0 });
  useEffect(() => {
    // Only signed-in users can hit /api/products now — skip fetch for guests.
    if (!user) return;
    api.get('/products', { params: { limit: 1 } })
      .then((r) => setMeta({ brands: r.data.brands || [], categories: r.data.categories || [], total: r.data.total || 0 }))
      .catch(() => {});
  }, [user]);
  const brandCount = meta.brands.length;
  const catCount = meta.categories.length;
  const productCount = meta.total;
  // Sample up to 14 brands for the "brands we carry" ticker, shuffled so no
  // single label dominates the top row.
  const brandDisplay = (() => {
    const shuffled = [...meta.brands].sort(() => Math.random() - 0.5).slice(0, 14);
    return shuffled;
  })();

  return (
    <div data-testid={IDS.home.hero}>
      {/* Hero — logo-forward */}
      <section
        style={{ background: 'var(--ije-black)', color: '#fff' }}
        className="relative overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div
            className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[85vw] h-[85vw] rounded-full"
            style={{ background: 'radial-gradient(closest-side, rgba(242,166,206,0.35), rgba(242,166,206,0) 70%)', filter: 'blur(60px)' }}
          />
          <div
            className="absolute -bottom-1/4 -left-1/4 w-[55vw] h-[55vw] rounded-full"
            style={{ background: 'radial-gradient(closest-side, rgba(194,71,138,0.22), rgba(194,71,138,0) 70%)', filter: 'blur(40px)' }}
          />
        </div>

        <div className="container-x relative z-10 pt-20 md:pt-24 pb-24 md:pb-32 flex flex-col items-center text-center">
          <div className="relative flex items-center justify-center w-full">
            <div className="ije-halo" aria-hidden="true" />
            <img
              src="/logo.png"
              alt="IJE HAUS LLC · Wholesale Distributor"
              className="relative h-48 sm:h-64 md:h-80 lg:h-[22rem] w-auto fade-up"
              style={{ userSelect: 'none' }}
              draggable="false"
              data-testid="home-hero-logo"
            />
          </div>

          <span
            className="eyebrow mt-10 fade-up fade-up-delay-1"
            style={{ color: 'var(--ije-pink-light)' }}
          >
            Trade &middot; Wholesale &middot; Curated
          </span>

          <h1 className="h-display text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-3xl fade-up fade-up-delay-2">
            A boutique wholesale storefront for
            <span className="pink-gradient-text italic"> intimate </span>
            product retailers.
          </h1>

          <p className="sans text-base md:text-lg text-neutral-300 mt-8 max-w-2xl leading-relaxed fade-up fade-up-delay-3">
            IJE HAUS curates a growing roster of top intimate-product brands for approved retail
            partners. Wholesale pricing, case-pack quantities, and drop-ship fulfillment — with none
            of the noise.
          </p>

          <div className="mt-10 flex flex-wrap gap-4 justify-center fade-up fade-up-delay-3">
            <Link to="/apply" className="btn-primary" data-testid={IDS.home.ctaApply}>
              Apply for Wholesale <ArrowRight size={16} />
            </Link>
            {user ? (
              <Link to="/products" className="btn-outline btn-outline--dark" data-testid={IDS.home.ctaBrowse}>
                Browse the Catalog
              </Link>
            ) : (
              <Link to="/login" className="btn-outline btn-outline--dark" data-testid={IDS.home.ctaBrowse}>
                <LogIn size={14} /> Sign In to Browse
              </Link>
            )}
          </div>

          {user && (brandCount > 0 || productCount > 0) && (
            <div className="mt-16 grid grid-cols-3 gap-8 md:gap-16 fade-up fade-up-delay-3">
              <div>
                <p className="serif text-4xl md:text-5xl pink-gradient-text">{productCount.toLocaleString()}</p>
                <p className="sans text-[0.65rem] tracking-[0.28em] uppercase text-neutral-400 mt-2">SKUs</p>
              </div>
              <div>
                <p className="serif text-4xl md:text-5xl pink-gradient-text">{brandCount}</p>
                <p className="sans text-[0.65rem] tracking-[0.28em] uppercase text-neutral-400 mt-2">Brands</p>
              </div>
              <div>
                <p className="serif text-4xl md:text-5xl pink-gradient-text">{catCount}</p>
                <p className="sans text-[0.65rem] tracking-[0.28em] uppercase text-neutral-400 mt-2">Categories</p>
              </div>
            </div>
          )}
          {!user && (
            <p className="sans text-xs tracking-[0.28em] uppercase text-neutral-500 mt-12 max-w-md fade-up fade-up-delay-3">
              The catalog is visible to approved trade partners only. Apply or sign in to browse.
            </p>
          )}
        </div>
      </section>

      {/* Trust bar */}
      <section className="container-x py-14 grid grid-cols-1 md:grid-cols-3 gap-8 border-b border-neutral-200">
        {[
          { icon: Layers, title: 'A Curated House', copy: 'Hand-selected brands across every category — from established houses to boutique labels we bring in ourselves.' },
          { icon: Truck, title: 'Drop-Ship Fulfillment', copy: 'No inventory held. We coordinate the supplier and ship direct to your buyer.' },
          { icon: ShieldCheck, title: 'Trade-Only Access', copy: 'Every account is reviewed. Approved retailers only see pricing.' },
        ].map((t) => (
          <div key={t.title} className="flex gap-5 items-start">
            <div className="pt-1">
              <t.icon size={22} strokeWidth={1.4} style={{ color: 'var(--ije-pink)' }} />
            </div>
            <div>
              <h3 className="serif text-xl">{t.title}</h3>
              <p className="sans text-sm text-neutral-500 mt-2 leading-relaxed">{t.copy}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Categories — only shown to signed-in users since the catalog is trade-only */}
      {user && (
        <section className="container-x py-24" data-testid={IDS.home.categoryGrid}>
          <div className="max-w-2xl mb-14">
            <span className="eyebrow">The Catalog</span>
            <h2 className="h-display text-3xl md:text-4xl mt-4">Shop by category.</h2>
            <p className="sans text-sm md:text-base text-neutral-500 mt-4 leading-relaxed">
              A tightly-edited selection across the categories our partners sell fastest.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {CATEGORIES.map((c) => (
              <Link
                key={c.name}
                to={`/products?category=${encodeURIComponent(c.name)}`}
                className="group relative overflow-hidden aspect-[3/4] block border border-neutral-200"
                data-testid={`home-cat-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(10,10,10,0.75) 100%)' }} />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="eyebrow" style={{ color: 'var(--ije-pink-light)' }}>Category</p>
                  <h3 className="serif text-xl md:text-2xl text-white mt-1">{c.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Brand row — dynamic from catalog, signed-in only */}
      {user && brandDisplay.length > 0 && (
        <section className="border-t border-neutral-200">
          <div className="container-x py-16">
            <div className="flex items-baseline justify-between gap-6 flex-wrap">
              <span className="eyebrow">Brands in the house</span>
              <Link to="/products" className="sans text-xs tracking-widest uppercase link-pink">
                Browse all {brandCount || 'brands'} →
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-10 gap-y-6" data-testid="home-brand-row">
              {brandDisplay.map((b) => (
                <Link
                  key={b}
                  to={`/products?brand=${encodeURIComponent(b)}`}
                  className="serif text-xl md:text-2xl italic text-neutral-700 hover:text-black transition-colors"
                >
                  {b}
                </Link>
              ))}
              <span className="serif text-xl md:text-2xl italic text-neutral-400">…and more.</span>
            </div>
            <p className="sans text-xs text-neutral-500 mt-6 leading-relaxed max-w-2xl">
              New labels are added on a rolling basis. Have a brand you want us to stock? Trade partners can
              request additions after signing in.
            </p>
          </div>
        </section>
      )}

      {/* Wholesale CTA */}
      <section style={{ background: 'var(--ije-black)', color: '#fff' }}>
        <div className="container-x py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow" style={{ color: 'var(--ije-pink-light)' }}>How it works</span>
            <h2 className="h-display text-3xl md:text-4xl mt-4">Four steps from application to drop-ship.</h2>
          </div>
          <ol className="space-y-6">
            {[
              ['Apply', 'Submit your business details, resale certificate, and monthly volume.'],
              ['Approval', 'We review within 1–2 business days and set your account discount tier.'],
              ['Quote', 'Browse the catalog, build a quote cart, submit your request for confirmation.'],
              ['Ship', 'We confirm the quote and coordinate drop-ship direct from the supplier.'],
            ].map(([step, copy], i) => (
              <li key={step} className="flex gap-6 items-start">
                <span className="serif text-3xl italic pink-gradient-text w-10 flex-shrink-0">0{i + 1}</span>
                <div>
                  <p className="sans text-sm tracking-[0.24em] uppercase text-white">{step}</p>
                  <p className="sans text-sm text-neutral-400 mt-2 leading-relaxed">{copy}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="hair hair--dark" />
        <div className="container-x py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="serif text-2xl md:text-3xl italic">Trade partner? Let&rsquo;s get you priced.</p>
            <p className="sans text-sm text-neutral-400 mt-2">Applications reviewed within 1–2 business days.</p>
          </div>
          <Link to="/apply" className="btn-primary flex items-center gap-3">
            <ClipboardList size={16} /> Apply for Wholesale Account
          </Link>
        </div>
      </section>
    </div>
  );
}
